import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from '../ui/use-toast';
import {
  User,
  MapPin,
  FileText,
  CheckCircle,
  Loader2,
  Truck
} from 'lucide-react';
import { AdminTransporterAPI, EnhancedTransporter } from '../../lib/api';
import {
  EnhancedDialog,
  EnhancedDialogContent,
  EnhancedDialogHeader,
  EnhancedDialogBody,
  EnhancedDialogFooter,
  EnhancedDialogTitle,
  EnhancedDialogDescription
} from '../ui/enhanced-dialog';
import { FormField, FormSection, FormGrid } from '../ui/form-field';

interface TransporterEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  transporter: EnhancedTransporter;
}

export default function TransporterEditModal({
  isOpen,
  onClose,
  onSuccess,
  transporter
}: TransporterEditModalProps) {
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    alternatePhone: '',
    address: {
      street: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India'
    },
    adminNotes: ''
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form data when transporter changes
  useEffect(() => {
    if (transporter) {
      setFormData({
        name: transporter.name || '',
        mobile: transporter.mobile || '',
        alternatePhone: transporter.alternatePhone || '',
        address: {
          street: transporter.address?.street || '',
          city: transporter.address?.city || '',
          state: transporter.address?.state || '',
          pincode: transporter.address?.pincode || '',
          country: transporter.address?.country || 'India'
        },
        adminNotes: transporter.adminNotes || ''
      });
    }
  }, [transporter]);

  // Update form data
  const updateFormData = (field: string, value: any) => {
    if (field.startsWith('address.')) {
      const addressField = field.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.mobile.trim()) newErrors.mobile = 'Mobile number is required';
    if (!formData.address.street.trim()) newErrors['address.street'] = 'Street address is required';
    if (!formData.address.city.trim()) newErrors['address.city'] = 'City is required';
    if (!formData.address.state.trim()) newErrors['address.state'] = 'State is required';
    if (!formData.address.pincode.trim()) newErrors['address.pincode'] = 'Pincode is required';

    // Mobile validation
    const mobileRegex = /^[6-9]\d{9}$/;
    if (formData.mobile && !mobileRegex.test(formData.mobile)) {
      newErrors.mobile = 'Please enter a valid 10-digit mobile number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fix the errors in the form",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      const response = await AdminTransporterAPI.updateTransporter(transporter._id, formData);

      if (response.success) {
        toast({
          title: "Success",
          description: "Transporter updated successfully",
          variant: "default"
        });

        handleClose();
        onSuccess();
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to update transporter",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error updating transporter:', error);
      toast({
        title: "Error",
        description: "Failed to update transporter. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  return (
    <EnhancedDialog open={isOpen} onOpenChange={handleClose}>
      <EnhancedDialogContent size="2xl">
        <EnhancedDialogHeader>
          <EnhancedDialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5" />
            Edit Transporter - {transporter?.name}
          </EnhancedDialogTitle>
          <EnhancedDialogDescription>
            Update transporter information and contact details
          </EnhancedDialogDescription>
        </EnhancedDialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <EnhancedDialogBody>
            {/* Personal Information */}
            <FormSection
              title="Personal Information"
              icon={<User className="h-5 w-5" />}
              description="Basic personal details of the transporter"
            >
              <FormGrid columns={2}>
                <FormField
                  label="Full Name"
                  required
                  error={errors.name}
                >
                  <Input
                    value={formData.name}
                    onChange={(e) => updateFormData('name', e.target.value)}
                    placeholder="Enter full name"
                  />
                </FormField>

                <FormField
                  label="Email Address"
                  hint="Email cannot be changed"
                >
                  <Input
                    type="email"
                    value={transporter?.email || ''}
                    disabled
                    className="bg-muted"
                  />
                </FormField>
              </FormGrid>

              <FormGrid columns={2}>
                <FormField
                  label="Mobile Number"
                  required
                  error={errors.mobile}
                >
                  <Input
                    value={formData.mobile}
                    onChange={(e) => updateFormData('mobile', e.target.value)}
                    placeholder="Enter mobile number"
                  />
                </FormField>

                <FormField
                  label="Alternate Phone"
                  hint="Optional alternate contact number"
                >
                  <Input
                    value={formData.alternatePhone}
                    onChange={(e) => updateFormData('alternatePhone', e.target.value)}
                    placeholder="Enter alternate phone"
                  />
                </FormField>
              </FormGrid>
            </FormSection>

            {/* Address Information */}
            <FormSection
              title="Address Information"
              icon={<MapPin className="h-5 w-5" />}
              description="Complete address details"
            >
              <FormField
                label="Street Address"
                required
                error={errors['address.street']}
              >
                <Input
                  value={formData.address.street}
                  onChange={(e) => updateFormData('address.street', e.target.value)}
                  placeholder="Enter street address"
                />
              </FormField>

              <FormGrid columns={2}>
                <FormField
                  label="City"
                  required
                  error={errors['address.city']}
                >
                  <Input
                    value={formData.address.city}
                    onChange={(e) => updateFormData('address.city', e.target.value)}
                    placeholder="Enter city"
                  />
                </FormField>

                <FormField
                  label="State"
                  required
                  error={errors['address.state']}
                >
                  <Input
                    value={formData.address.state}
                    onChange={(e) => updateFormData('address.state', e.target.value)}
                    placeholder="Enter state"
                  />
                </FormField>
              </FormGrid>

              <FormGrid columns={2}>
                <FormField
                  label="Pincode"
                  required
                  error={errors['address.pincode']}
                >
                  <Input
                    value={formData.address.pincode}
                    onChange={(e) => updateFormData('address.pincode', e.target.value)}
                    placeholder="Enter pincode"
                  />
                </FormField>

                <FormField
                  label="Country"
                  required
                >
                  <Input
                    value={formData.address.country}
                    onChange={(e) => updateFormData('address.country', e.target.value)}
                    placeholder="Enter country"
                  />
                </FormField>
              </FormGrid>
            </FormSection>

            {/* Admin Notes */}
            <FormSection
              title="Admin Notes"
              icon={<FileText className="h-5 w-5" />}
              description="Internal notes and comments"
              collapsible
              defaultExpanded={false}
            >
              <FormField
                label="Administrative Notes"
                hint="Add any administrative notes or comments"
              >
                <Textarea
                  value={formData.adminNotes}
                  onChange={(e) => updateFormData('adminNotes', e.target.value)}
                  placeholder="Add any administrative notes..."
                  rows={3}
                />
              </FormField>
            </FormSection>
          </EnhancedDialogBody>

          <EnhancedDialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Update Transporter
                </>
              )}
            </Button>
          </EnhancedDialogFooter>
        </form>
      </EnhancedDialogContent>
    </EnhancedDialog>
  );
}